
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface OrderItem {
  id: string;
  quantity: number;
  price: number;
  products: {
    id: string;
    name: string;
    image_url: string | null;
  };
}

interface OrderDetails {
  id: string;
  created_at: string;
  total_amount: number;
  status: string;
  payment_status: string;
  payment_method: string;
  shipping_address: any;
  billing_address: any;
  tracking_number: string | null;
  order_number: string | null;
  customer_name: string | null;
  customer_email: string | null;
  order_items: OrderItem[];
  profiles: {
    full_name: string;
    email: string;
  } | null;
}

export const useOrderConfirmation = (orderId?: string) => {
  const { user } = useAuth();

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order-confirmation', orderId],
    queryFn: async () => {
      if (!orderId) {
        // Get the most recent order for the user
        const { data, error } = await supabase
          .from('orders')
          .select(`
            *,
            profiles (
              full_name,
              email
            ),
            order_items (
              id,
              quantity,
              price,
              products (
                id,
                name,
                image_url
              )
            )
          `)
          .eq('user_id', user?.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        
        // Transform the data to match our interface
        if (data) {
          return {
            ...data,
            profiles: data.profiles && typeof data.profiles === 'object' && !Array.isArray(data.profiles) 
              ? {
                  full_name: (data.profiles as any)?.full_name || 'Unknown User',
                  email: (data.profiles as any)?.email || 'No email'
                }
              : null
          } as OrderDetails;
        }
        
        return null;
      }

      // Get specific order by ID
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          profiles (
            full_name,
            email
          ),
          order_items (
            id,
            quantity,
            price,
            products (
              id,
              name,
              image_url
            )
          )
        `)
        .eq('id', orderId)
        .maybeSingle();

      if (error) throw error;
      
      // Transform the data to match our interface
      if (data) {
        return {
          ...data,
          profiles: data.profiles && typeof data.profiles === 'object' && !Array.isArray(data.profiles)
            ? {
                full_name: (data.profiles as any)?.full_name || 'Unknown User',
                email: (data.profiles as any)?.email || 'No email'
              }
            : null
        } as OrderDetails;
      }
      
      return null;
    },
    enabled: !!user,
  });

  return {
    order,
    isLoading,
    error,
  };
};
